import React, { useEffect, useRef, useState } from 'react';
import { FiChevronDown, FiClock, FiSearch, FiX } from 'react-icons/fi';
import { useSearch } from '../contexts/SearchContext';
import './SearchInputWithHistory.css';

/**
 * Gedeelde zoekbalk: recente zoekopdrachten (dropdown) + wis-knop.
 * variant: 'navbar' | 'magazijn'
 */
export default function SearchInputWithHistory({
  variant = 'navbar',
  placeholder = 'Zoeken...',
  inputRef: externalInputRef,
  autoComplete = 'off',
}) {
  const { searchTerm, setSearchTerm, recentSearches, commitRecentSearch } = useSearch();
  const [historyOpen, setHistoryOpen] = useState(false);
  const wrapRef = useRef(null);
  const localInputRef = useRef(null);
  const inputRef = externalInputRef || localInputRef;

  useEffect(() => {
    if (!historyOpen) return undefined;
    const onDocClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setHistoryOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [historyOpen]);

  const handleClear = () => {
    setSearchTerm('');
    setHistoryOpen(false);
    inputRef.current?.focus();
  };

  const handleSelectRecent = (term) => {
    setSearchTerm(term);
    commitRecentSearch(term);
    setHistoryOpen(false);
    inputRef.current?.focus();
  };

  const boxClass =
    variant === 'magazijn'
      ? 'magazijn-search search-input-with-history search-input-with-history--magazijn'
      : 'navbar-search-box search-input-with-history search-input-with-history--navbar';

  const inputClass =
    variant === 'magazijn' ? 'magazijn-search-input search-input-field' : 'navbar-search-input search-input-field';

  const iconClass = variant === 'magazijn' ? 'magazijn-search-icon' : 'navbar-search-icon';

  const hasRecent = recentSearches.length > 0;
  const hasValue = Boolean(searchTerm);

  return (
    <div ref={wrapRef} className={boxClass}>
      <FiSearch className={iconClass} aria-hidden />
      <input
        ref={inputRef}
        type="text"
        className={inputClass}
        placeholder={placeholder}
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        onFocus={() => {
          if (hasRecent) setHistoryOpen(true);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Escape') setHistoryOpen(false);
        }}
        autoComplete={autoComplete}
        aria-expanded={historyOpen}
        aria-haspopup="listbox"
      />
      {hasRecent ? (
        <button
          type="button"
          className="search-history-toggle"
          onClick={() => setHistoryOpen((v) => !v)}
          title="Recente zoekopdrachten"
          aria-label="Recente zoekopdrachten"
          aria-expanded={historyOpen}
        >
          <FiClock aria-hidden />
          <FiChevronDown className={`search-history-chevron${historyOpen ? ' is-open' : ''}`} aria-hidden />
        </button>
      ) : null}
      {hasValue ? (
        <button
          type="button"
          className="search-input-clear"
          onClick={handleClear}
          title="Wis zoekopdracht"
          aria-label="Wis zoekopdracht"
        >
          <FiX aria-hidden />
        </button>
      ) : null}
      {historyOpen && hasRecent ? (
        <ul className="search-history-dropdown" role="listbox" aria-label="Recente zoekopdrachten">
          <li className="search-history-dropdown-label">Recent</li>
          {recentSearches.map((term) => (
            <li key={term}>
              <button
                type="button"
                className="search-history-item"
                role="option"
                onClick={() => handleSelectRecent(term)}
              >
                {term}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
